import { Box, Card, Text, colors } from '@factorialco/deprecated-design-system'
import { ResponsiveLine } from '@nivo/line'
import { Currency } from 'dinero.js'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import DataIndicator from 'modules/finance/components/DataIndicator'

import SliceTooltip from './_SliceTooltip'

interface GraphPoint {
  x: Date
  y: number | null
}
interface Props {
  data: GraphPoint[]
  serieId: string
  title: string
  subtitle: string
  currentValue: string
  valueChange?: number
  currency: Currency
}

const LineChart = ({
  data,
  title,
  subtitle,
  serieId,
  currentValue,
  valueChange,
  currency,
}: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const seriesData = [
    {
      id: serieId,
      data: data.filter((point, index) =>
        // filter null values between the start/end dates to avoid gaps
        index > 0 && index < data.length - 1 ? point.y !== null : true
      ),
    },
  ]

  return (
    <Card>
      <Card.Title>{title}</Card.Title>
      <Card.Content>
        <Box paddingBottom="s16" gap="s16">
          <Box paddingTop="s24" gap="s8">
            <Text size="300" weight="medium" color="grey800">
              {subtitle}
            </Text>
            <Text size="500" weight="semibold" color="grey1000">
              {currentValue}
            </Text>
            {valueChange !== undefined && valueChange !== 0 && (
              <Box flexDirection="row" gap="s2" alignItems="center">
                <DataIndicator change={valueChange} />
                <Box gap="s4" flexDirection="row">
                  <Text weight="medium" size="200" color="grey800">
                    {MoneyWithCurrency({ amount: Math.abs(valueChange), currency, locale })}
                  </Text>
                  <Text weight="medium" size="200" color="grey800">
                    {i18n.t(
                      'it_management.software_setups.dashboard.charts.cost_per_month.from_last'
                    )}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
          <Text size="100" weight="medium">
            {currency}
          </Text>
        </Box>
        <Box height="s200" width="full">
          <ResponsiveLine
            /* DATA & AXES */
            data={seriesData}
            xScale={{
              format: '%m',
              precision: 'month',
              type: 'time',
              useUTC: false,
            }}
            axisBottom={{
              tickSize: 0,
              tickPadding: 14,
              tickValues: 'every month',
              format: (value) => moment(value).format('MMM')[0],
            }}
            yScale={{
              type: 'linear',
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 14,
              tickValues: 3,
            }}
            enableGridY
            enableGridX={false}
            gridYValues={3}
            /* POINTS */
            enablePoints
            pointSize={7}
            pointBorderWidth={2}
            pointLabelYOffset={-12}
            pointSymbol={(p) => (
              <circle
                cx="0"
                cy="0"
                r="3"
                stroke={p.color}
                strokeWidth="2"
                fill={colors.grey000White}
              />
            )}
            /* INTERACTIVITY */
            useMesh
            isInteractive
            enableSlices="x"
            sliceTooltip={(props) => <SliceTooltip {...props} currency={currency} />}
            /* STYLE */
            enableArea
            defs={[
              {
                colors: [
                  { offset: 0, color: colors.viridian1000 },
                  { offset: 80, color: '#39BBC500' },
                ],
                id: 'gradientA',
                type: 'linearGradient',
              },
            ]}
            fill={[
              {
                id: 'gradientA',
                match: '*',
              },
            ]}
            theme={{
              grid: {
                line: {
                  stroke: '#E2E2E5',
                  strokeWidth: 1,
                  strokeDasharray: '4 4',
                },
              },
            }}
            markers={[
              {
                axis: 'y',
                value: 0,
                lineStyle: {
                  stroke: colors.grey600,
                  strokeWidth: 1,
                },
              },
            ]}
            margin={{ left: 40, top: 10, bottom: 40, right: 30 }}
            colors={colors.viridian1000}
          />
        </Box>
      </Card.Content>
    </Card>
  )
}

export default LineChart
